/**
 * grass_source.ts — the grass field's view of the WORLD (2026-09-25, visual-realism R2).
 *
 * Adapts the game's samplers to `GrassFieldSource`: the rendered height (meshHeight — exact where a
 * chunk is resident, which `covered` insists on), the biome weights with Edit Mode paint, the woods'
 * canopy, the road mask, the pad/port clearance, structures and cave aprons (blocked), the sea / baked
 * lakes / rivers, and two value noises (meadow patches ~30 m, dryness ~90 m). Biome weights and
 * canopy vary over hundreds of metres, so they are sampled on a 16 m lattice and cached; the road,
 * clearance and water tests run per 2 m sample (the rules' lattice).
 */
import { biomeWeights, BIOMES, riverWaterAt } from '@shared/terrain.js';
import { caveSitesNear, CAVE_APRON_R, CAVE_APRON_RING } from '@shared/caves.js';
import { meshHeight, roadSurfaceMask, settlementClearance } from '../terrain_stamp.js';
import { paintedWeights } from '../terrain_edits.js';
import { lakeSurfaceAtBaked } from '../lakes.js';
import { forestDensity, forestNoise, nearestSiteDist } from '../forest_field.js';
import type { GrassFieldSource } from './grass_field.js';
import { insideGroundKeepOut, type KeepShape } from '../ground_keepout.js';
import type { GrassSample } from './grass_rules.js';
import { CHUNK_SIZE } from '../../config.js';

const LAT_M = 16;
/** Settlement clearance is only ever non-null inside a plan's reach — skip it beyond this from every centre. */
const SITE_REACH_M = 1600;
/** Cave mouths keep a grass-free apron (the portal outcrop stands there). */
const CAVE_KEEP_M = CAVE_APRON_R + CAVE_APRON_RING + 6;

export interface GrassSourceHooks {
  seed: string;
  sea: number;
  /** Is the chunk (cx, cz) resident (its rendered grid registered with meshHeight)? */
  chunkReady(cx: number, cz: number): boolean;
  /** Placed-structure discs near a point (x, z, r²) — the building keep-out. */
  structures?(x0: number, z0: number, x1: number, z1: number): Array<{ x: number; z: number; r2: number }>;
  /**
   * The ground's climate channel at (x, z) off the resident chunk mesh — [tint r, g, b, dryness, wetness] into
   * `out` — false where no v2 chunk data is resident. The turf colour then comes from the ground's own inputs.
   */
  groundAt?(x: number, z: number, out: Float32Array): boolean;
  /** THE GROUND KEEP-OUT (lib/ground_keepout.ts): every standing building and interior shape reaching a rect. */
  keepOuts?(x0: number, z0: number, x1: number, z1: number): KeepShape[];
}

export function makeGrassSource(h: GrassSourceHooks): GrassFieldSource & { clearCache(): void } {
  const lat = new Map<number, Float32Array>(); // (lattice key) → [w0..w5, forest]
  const gbuf = new Float32Array(5); // the ground's [tint rgb, dry, wet] — consumed by the rule at once
  const key = (ix: number, iz: number): number => (ix + 0x100000) * 0x200000 + (iz + 0x100000);
  const latAt = (ix: number, iz: number): Float32Array => {
    const k = key(ix, iz);
    let v = lat.get(k);
    if (!v) {
      const x = ix * LAT_M, z = iz * LAT_M;
      const bw = biomeWeights(h.seed, x, z);
      const pw = paintedWeights(BIOMES.map((b) => bw[b]), x, z);
      v = new Float32Array(7);
      for (let i = 0; i < 6; i++) v[i] = pw[i];
      v[6] = forestDensity(h.seed, x, z, h.sea);
      if (lat.size > 20000) lat.clear();
      lat.set(k, v);
    }
    return v;
  };
  let tileStructures: Array<{ x: number; z: number; r2: number }> = [];
  let tileCaves: Array<{ x: number; z: number }> = [];
  let tileNearSite = false;
  let tileKey = '';
  const prepareTile = (x: number, z: number): void => {
    const tx = Math.floor(x / 8), tz = Math.floor(z / 8); const k = `${tx},${tz}`;
    if (k === tileKey) return;
    tileKey = k;
    const x0 = tx * 8, z0 = tz * 8;
    tileStructures = h.structures ? h.structures(x0 - 2, z0 - 2, x0 + 10, z0 + 10) : [];
    tileCaves = caveSitesNear(h.seed, x0, z0, x0 + 8, z0 + 8, CAVE_KEEP_M + 2).map((c) => ({ x: c.x, z: c.z }));
    tileNearSite = nearestSiteDist(h.seed, x0 + 4, z0 + 4) < SITE_REACH_M;
  };
  return {
    covered(x0, z0, x1, z1): boolean {
      // the ±1 m slope taps reach one metre past the tile — at a chunk edge its neighbour must be resident too
      for (let cz = Math.floor((z0 - 1) / CHUNK_SIZE); cz <= Math.floor((z1 + 1) / CHUNK_SIZE); cz++) for (let cx = Math.floor((x0 - 1) / CHUNK_SIZE); cx <= Math.floor((x1 + 1) / CHUNK_SIZE); cx++) if (!h.chunkReady(cx, cz)) return false;
      return true;
    },
    height: (x, z) => meshHeight(h.seed, x, z),
    sample(x: number, z: number, _h: number, _slope: number, out: GrassSample): void {
      prepareTile(x, z);
      // biome weights + canopy: bilinear on the 16 m lattice
      const fx = x / LAT_M, fz = z / LAT_M, ix = Math.floor(fx), iz = Math.floor(fz), ax = fx - ix, az = fz - iz;
      const a = latAt(ix, iz), b = latAt(ix + 1, iz), c = latAt(ix, iz + 1), d = latAt(ix + 1, iz + 1);
      const w = out.w as Float32Array;
      for (let i = 0; i < 6; i++) w[i] = (a[i] * (1 - ax) + b[i] * ax) * (1 - az) + (c[i] * (1 - ax) + d[i] * ax) * az;
      out.forest = (a[6] * (1 - ax) + b[6] * ax) * (1 - az) + (c[6] * (1 - ax) + d[6] * ax) * az;
      out.road = roadSurfaceMask(h.seed, x, z);
      let blocked = false;
      for (const s of tileStructures) if ((s.x - x) ** 2 + (s.z - z) ** 2 < s.r2) { blocked = true; break; }
      if (!blocked) for (const cv of tileCaves) if ((cv.x - x) ** 2 + (cv.z - z) ** 2 < CAVE_KEEP_M * CAVE_KEEP_M) { blocked = true; break; }
      if (!blocked && tileNearSite && settlementClearance(h.seed, x, z) !== null) blocked = true;
      out.blocked = blocked;
      out.sea = h.sea;
      const lake = lakeSurfaceAtBaked(x, z);
      const river = riverWaterAt(h.seed, x, z);
      out.water = lake !== null && river !== null ? Math.max(lake, river) : (lake ?? river);
      out.patch = 0.62 * forestNoise(h.seed, 'grassPatch', x, z, 31) + 0.38 * forestNoise(h.seed, 'grassPatch2', x, z, 11);
      out.dryN = forestNoise(h.seed, 'grassDry', x, z, 90);
      out.ground = h.groundAt && h.groundAt(x, z, gbuf) ? gbuf : null;
    },
    tileKeepOut(x0, z0, x1, z1) {
      if (!h.keepOuts) return null;
      const list = h.keepOuts(x0, z0, x1, z1);
      return list.length ? (x: number, z: number, hy: number): boolean => insideGroundKeepOut(list, x, z, hy) : null;
    },
    clearCache(): void { lat.clear(); tileKey = ''; },
  };
}
